import { HEALTH_TIMINGS } from '../health/timings'
import type { LiveEnvelope, LiveStatus } from './types'

/** Минимум WebSocket, который нужен каналу: браузерный и тестовый подставной одинаково годятся. */
export interface LiveSocket {
  onopen: ((event: Event) => void) | null
  onmessage: ((event: MessageEvent) => void) | null
  onclose: ((event: CloseEvent) => void) | null
  onerror: ((event: Event) => void) | null
  close(code?: number, reason?: string): void
}

/** Коды закрытия сервера (Platform `LiveCloseCodes`): договор, не стиль. */
export const LIVE_CLOSE = {
  /** Истёк токен соединения: переподключиться сразу, шлюз продлит сессию или ответит 401. */
  sessionExpired: 4401,
  /** Клиент не успевал забирать события: переподключиться сразу и перечитать. */
  lagging: 4408,
} as const

/** Первое сообщение сервера: вход и права проверены, события пойдут. */
export const LIVE_READY = 'live.ready'

export interface LiveChannelOptions {
  /** Адрес канала (`wss://<origin>/api/platform/v1/live`). */
  url: () => string
  /** Открыть сокет. В браузере — `new WebSocket(url)`, в тесте — подставной. */
  connect: (url: string) => LiveSocket
  onEnvelope: (envelope: LiveEnvelope) => void
  onStatus: (status: LiveStatus) => void
  /** Подключение восстановлено после обрыва: за время обрыва события могли потеряться. */
  onResync: () => void
  /**
   * Попытка не дошла до «готов» (отказ upgrade: 401/403/429 браузер не показывает
   * и сеть недоступна выглядят одинаково). Оболочка спрашивает «кто я»: истёкшая
   * сессия остановит канал, а не даст ему стучаться без конца.
   */
  onConnectFailed: (attempt: number) => void
  setTimer?: (callback: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
  random?: () => number
}

export interface LiveChannel {
  /** Включить канал (идемпотентно). */
  start(): void
  /** Выключить канал: закрыть соединение и не переподключаться. */
  stop(): void
  readonly status: LiveStatus
}

/**
 * Разобрать сообщение сервера. Чужая форма — `null`: канал не падает от
 * сообщения, которого не понимает, — его просто пропускает.
 */
export function decodeLiveEnvelope(data: unknown): LiveEnvelope | null {
  if (typeof data !== 'string' || data.length > 64 * 1024) return null
  let raw: unknown
  try {
    raw = JSON.parse(data)
  } catch {
    return null
  }
  if (typeof raw !== 'object' || raw === null) return null
  const value = raw as Record<string, unknown>
  if (typeof value.type !== 'string' || value.type === '' || typeof value.at !== 'string') return null
  const optional = (field: unknown) => (typeof field === 'string' && field !== '' ? field : null)
  const payload =
    typeof value.payload === 'object' && value.payload !== null && !Array.isArray(value.payload)
      ? (value.payload as Record<string, unknown>)
      : null
  return { type: value.type, resourceId: optional(value.resourceId), kind: optional(value.kind), at: value.at, payload }
}

/**
 * Одно соединение живого канала на вкладку: подключение, «готов», события,
 * переподключение с нарастающей паузой. Сам канал ничего не знает о Redux и
 * тегах: что делать с событием, решает оболочка (`onEnvelope`).
 */
export function createLiveChannel(options: LiveChannelOptions): LiveChannel {
  const setTimer = options.setTimer ?? ((callback: () => void, ms: number) => setTimeout(callback, ms))
  const clearTimer = options.clearTimer ?? ((handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>))
  const random = options.random ?? Math.random

  let status: LiveStatus = 'off'
  let wanted = false
  let socket: LiveSocket | null = null
  let timer: unknown = null
  /** Попыток подряд без «готов». */
  let failures = 0
  /** Было ли хоть одно «готов» с момента start: следующее «готов» — переподключение, нужно перечитать. */
  let everReady = false

  const setStatus = (next: LiveStatus) => {
    if (status === next) return
    status = next
    options.onStatus(next)
  }

  const delay = (attempt: number) => {
    // Паузы — `HEALTH_TIMINGS.liveBackoffMs`; разброс ±20 %, чтобы вкладки не шли строем.
    const pauses = HEALTH_TIMINGS.liveBackoffMs
    const base = pauses[Math.min(attempt, pauses.length - 1)] ?? 30_000
    return Math.round(base * (0.8 + random() * 0.4))
  }

  const schedule = (ms: number) => {
    if (timer !== null) clearTimer(timer)
    timer = setTimer(() => {
      timer = null
      open()
    }, ms)
  }

  const open = () => {
    if (!wanted) return
    let ready = false
    let current: LiveSocket
    try {
      current = options.connect(options.url())
    } catch {
      failures += 1
      options.onConnectFailed(failures)
      setStatus('reconnecting')
      schedule(delay(failures - 1))
      return
    }
    socket = current
    current.onmessage = (event) => {
      const envelope = decodeLiveEnvelope(event.data as unknown)
      if (envelope === null) return
      if (envelope.type === LIVE_READY) {
        ready = true
        failures = 0
        setStatus('online')
        if (everReady) options.onResync()
        everReady = true
        return
      }
      options.onEnvelope(envelope)
    }
    current.onerror = () => {
      // Подробностей браузер не даёт; итог скажет onclose.
    }
    current.onclose = (event) => {
      if (socket !== current) return
      socket = null
      if (!wanted) return
      setStatus('reconnecting')
      if (ready && (event.code === LIVE_CLOSE.sessionExpired || event.code === LIVE_CLOSE.lagging)) {
        // Плановое закрытие сервером: сразу заново (шлюз продлит сессию), с малым разбросом.
        schedule(Math.round(random() * 250))
        return
      }
      if (!ready) {
        failures += 1
        options.onConnectFailed(failures)
      }
      schedule(delay(ready ? 0 : failures - 1))
    }
  }

  return {
    start() {
      if (wanted) return
      wanted = true
      failures = 0
      everReady = false
      setStatus('connecting')
      open()
    },
    stop() {
      wanted = false
      if (timer !== null) {
        clearTimer(timer)
        timer = null
      }
      const current = socket
      socket = null
      current?.close(1000, 'stopped')
      setStatus('off')
    },
    get status() {
      return status
    },
  }
}
